//@flow
import React from 'react';

import {
    Box,
    Stack,
    Columns,
    Column,
    Text,
    Icon as NeoIcon
} from '@locus-taxy/neo-ui';
import { neoUIColors } from 'common/enums/ColorEnum';
import UnallocatedEditsCollapseComponent from './UnallocatedEditsCollapseComponent';
import AllocatedEditsCollapseComponent from './AllocatedEditsCollapseComponent';
import MovedEditsCollapseComponent from './MovedEditsCollapseComponent';
import { collapsibleListCardFactory } from 'common/components/core/card/CollapsibleListCard';
import theme from './PlanEditsDialog.scss';
import type { TFunction } from 'react-i18next';
import { FpSalesbeatPlan } from 'common/models/internal/FpSalesbeatPlan';
import { FpPjPlan } from 'common/models/internal/FpPjPlan';

const EditTypes = Object.freeze({
    Move: 'MOVE',
    Unallocation: 'UNALLOCATION',
    Allocated: 'ALLOCATION'
});

type CardTitleProps = {
    title: string,
    toggleUnallocationEditsCard: () => void
};
class CardTitle extends React.PureComponent<CardTitleProps> {
    render() {
        const { title, toggleUnallocationEditsCard } = this.props;
        return (
            <Box p={2} height="16px" onClick={toggleUnallocationEditsCard}>
                <Columns alignY="center" height="fluid" width="fluid">
                    <Column width="fluid">
                        <Text fontSize={2} fontWeight={600}>
                            {title}
                        </Text>
                    </Column>
                </Columns>
            </Box>
        );
    }
}

const CollapsibleUnallocationsCard = collapsibleListCardFactory({
    CardComponent: CardTitle,
    CollapseComponent: UnallocatedEditsCollapseComponent
});

const CollapsibleAllocationsCard = collapsibleListCardFactory({
    CardComponent: CardTitle,
    CollapseComponent: AllocatedEditsCollapseComponent
});

const CollapsibleMoveVisitCard = collapsibleListCardFactory({
    CardComponent: CardTitle,
    CollapseComponent: MovedEditsCollapseComponent
});

type PlanEditDialogProps = {
    salesbeatPlan: FpSalesbeatPlan,
    t: TFunction,
    plan: FpPjPlan,
    closeEditsDialog: () => void
};

export default class PlanEditsDialog extends React.Component<PlanEditDialogProps> {
    constructor(props) {
        super(props);
        this.state = {
            unallocationEditsExpanded: false,
            allocationEditsExpanded: false,
            movedEditsExpanded: false
        };
    }

    toggleUnallocationEditsCard = () => {
        const { unallocationEditsExpanded } = this.state;
        this.setState({
            unallocationEditsExpanded: !unallocationEditsExpanded
        });
    };

    toggleAllocationEditsCard = () => {
        const { allocationEditsExpanded } = this.state;
        this.setState({
            allocationEditsExpanded: !allocationEditsExpanded
        });
    };

    toggleMovedEditsCard = () => {
        const { movedEditsExpanded } = this.state;
        this.setState({
            movedEditsExpanded: !movedEditsExpanded
        });
    };

    getUnallocationEdits = () => {
        const { appliedEdits } = this.props.salesbeatPlan;
        const unallocationEdits = appliedEdits.filter(edit => {
            return edit.edits[0].editType === EditTypes.Unallocation;
        });
        return unallocationEdits;
    };

    getAllocationEdits = () => {
        const { appliedEdits } = this.props.salesbeatPlan;
        const allocationEdits = appliedEdits.filter(edit => {
            return edit.edits[0].editType === EditTypes.Allocated;
        });
        return allocationEdits;
    };

    getMovedVisitEdits = () => {
        const { appliedEdits } = this.props.salesbeatPlan;

        const movedEdits = appliedEdits.filter(edit => {
            return edit.edits[0].editType === EditTypes.Move;
        });
        return movedEdits;
    };

    render() {
        const {
            unallocationEditsExpanded,
            allocationEditsExpanded,
            movedEditsExpanded
        } = this.state;
        const { salesbeatPlan, closeEditsDialog, plan, t } = this.props;
        const { summary } = salesbeatPlan;
        const totalEdits = summary?.editsSummary?.editCount || 0;
        const unallocationEdits = this.getUnallocationEdits();
        const allocationEdits = this.getAllocationEdits();
        const movedVisitEdits = this.getMovedVisitEdits();
        return (
            <Box height="400px">
                <Box p={2}>
                    <Box height="60px">
                        <Stack space="small">
                            <Box>
                                <Columns
                                    space="small"
                                    width="fluid"
                                    height="fluid"
                                    alignY="center"
                                >
                                    <Column width="content">
                                        <NeoIcon
                                            icon="dispatcher_drop"
                                            size="large"
                                        />
                                    </Column>
                                    <Column>
                                        <Stack>
                                            <Text fontSize={3} fontWeight={600}>
                                                {plan.id}
                                            </Text>
                                            <Text
                                                fontSize={1}
                                                color={neoUIColors.grey200}
                                            >
                                                {t('fpPlanView.visitsChanged', {
                                                    visitsChanged: totalEdits
                                                })}
                                            </Text>
                                        </Stack>
                                    </Column>
                                    <Column width="content">
                                        <NeoIcon
                                            size="medium"
                                            icon="cross"
                                            onClick={closeEditsDialog}
                                        />
                                    </Column>
                                </Columns>
                            </Box>
                            <Box>
                                <CollapsibleUnallocationsCard
                                    t={t}
                                    theme={theme}
                                    toggleUnallocationEditsCard={
                                        this.toggleUnallocationEditsCard
                                    }
                                    isExpandable
                                    rightIcon
                                    isExpanded={unallocationEditsExpanded}
                                    title={t('fpPlanView.unallocations')}
                                    unallocationEdits={unallocationEdits}
                                />
                            </Box>
                            <Box>
                                <CollapsibleAllocationsCard
                                    t={t}
                                    beats={salesbeatPlan.beats}
                                    theme={theme}
                                    toggleUnallocationEditsCard={
                                        this.toggleAllocationEditsCard
                                    }
                                    isExpandable
                                    isExpanded={allocationEditsExpanded}
                                    title={t('fpPlanView.allocations')}
                                    allocationEdits={allocationEdits}
                                />
                            </Box>
                            <Box>
                                <CollapsibleMoveVisitCard
                                    t={t}
                                    theme={theme}
                                    toggleUnallocationEditsCard={
                                        this.toggleMovedEditsCard
                                    }
                                    isExpandable
                                    isExpanded={movedEditsExpanded}
                                    title={t('fpPlanView.moved')}
                                    movedEdits={movedVisitEdits}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        );
    }
}
